import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import type { Session } from '@supabase/supabase-js'
import { supabase } from '../lib/supabase'
import { normalizeSriLankanMobile } from '../lib/phoneValidation'
import type { Profile } from '../types'

type AuthContextValue = {
  loading: boolean
  session: Session | null
  profile: Profile | null
  isAuthenticated: boolean
  isAdmin: boolean
  signIn: (email: string, password: string, captchaToken: string) => Promise<string | null>
  signUp: (payload: { fullName: string; email: string; password: string; phone:string; educationLevel:string }, captchaToken: string) => Promise<{ error:string|null; signedIn:boolean; confirmationRequired:boolean }>
  signOut: () => Promise<void>
  refreshProfile: () => Promise<void>
  updateProfile: (changes: Partial<Pick<Profile,'full_name'|'programme'|'academic_year'|'semester'|'phone'|'address'|'bio'>>) => Promise<string | null>
}

const AuthContext = createContext<AuthContextValue | null>(null)

function toProfile(data:any, session:Session): Profile {
  return {
    id:data?.id ?? session.user.id,
    full_name:data?.full_name ?? session.user.user_metadata?.full_name ?? session.user.email?.split('@')[0] ?? 'Student',
    email:data?.email ?? session.user.email ?? '',
    role:data?.role ?? 'student',
    programme:data?.programme ?? session.user.user_metadata?.programme ?? null,
    academic_year:data?.academic_year ?? null,
    semester:data?.semester ?? null,
    avatar_url:data?.avatar_url ?? null,
    phone:data?.phone ?? null,
    address:data?.address ?? null,
    bio:data?.bio ?? null,
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(true)
  const [session, setSession] = useState<Session | null>(null)
  const [profile, setProfile] = useState<Profile | null>(null)

  const loadProfile = async (current: Session | null) => {
    setSession(current)
    if (!current?.user) { setProfile(null); setLoading(false); return }
    const { data, error } = await supabase.from('profiles').select('*').eq('id', current.user.id).maybeSingle()
    if (error) console.error('Profile load failed:', error.message)
    setProfile(toProfile(data,current))
    setLoading(false)
  }

  useEffect(() => {
    let active=true
    supabase.auth.getSession().then(({data})=>{if(active) void loadProfile(data.session)})
    const {data:listener}=supabase.auth.onAuthStateChange((_event,next)=>{if(active) void loadProfile(next)})
    return()=>{active=false;listener.subscription.unsubscribe()}
  },[])

  const value = useMemo<AuthContextValue>(() => ({
    loading, session, profile,
    isAuthenticated:Boolean(session?.user),
    isAdmin:Boolean(profile && ['moderator','admin','super_admin'].includes(profile.role)),
    signIn:async(email,password,captchaToken)=>{
      if(import.meta.env.VITE_TURNSTILE_SITE_KEY&&!captchaToken)return 'Complete human verification first.'
      const {error}=await supabase.auth.signInWithPassword({email:email.trim().toLowerCase(),password,options:captchaToken?{captchaToken}:{}})
      return error?.message ?? null
    },
    signUp:async({fullName,email,password,phone,educationLevel},captchaToken)=>{
      const normalizedPhone=normalizeSriLankanMobile(phone)
      if(!normalizedPhone)return {error:'Enter a valid Sri Lankan mobile number.',signedIn:false,confirmationRequired:false}
      if(import.meta.env.VITE_TURNSTILE_SITE_KEY&&!captchaToken)
        return {error:'Complete human verification first.',signedIn:false,confirmationRequired:false}
      const redirectTo=`${import.meta.env.VITE_APP_URL || window.location.origin}/app`
      const parts=fullName.trim().split(/\s+/)
      const {data,error}=await supabase.auth.signUp({
        email:email.trim().toLowerCase(),password,
        options:{emailRedirectTo:redirectTo,...(captchaToken?{captchaToken}:{}),data:{
          full_name:fullName.trim(),first_name:parts[0]||'Student',last_name:parts.slice(1).join(' ')||'User',
          education_level:educationLevel,phone:normalizedPhone
        }}
      })
      if(error)return {error:error.message,signedIn:false,confirmationRequired:false}
      // When Supabase email confirmation is OFF a real session is issued immediately.
      // Do not navigate unless a valid session exists; never issue a second login request
      // with a spent CAPTCHA token or pretend an unconfirmed account is authenticated.
      if(data.session?.user){
        await loadProfile(data.session)
        return {error:null,signedIn:true,confirmationRequired:false}
      }
      return {error:null,signedIn:false,confirmationRequired:true}
    },
    signOut:async()=>{await supabase.auth.signOut();setSession(null);setProfile(null)},
    refreshProfile:async()=>{const {data}=await supabase.auth.getSession();await loadProfile(data.session)},
    updateProfile:async(changes)=>{
      if(!profile)return 'No signed-in profile.'
      const payload={...changes,updated_at:new Date().toISOString()}
      const {data,error}=await supabase.from('profiles').update(payload).eq('id',profile.id).select('*').single()
      if(error)return error.message
      setProfile(toProfile(data,session!))
      return null
    }
  }),[loading,profile,session])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(){const value=useContext(AuthContext);if(!value)throw new Error('useAuth must be used inside AuthProvider');return value}
